import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

export interface Workspace {
  _id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  memberCount?: number;
  projectCount?: number;
  activeProjectCount?: number;
  completedProjectCount?: number;
}

export interface WorkspaceMember {
  _id: string;
  email: string;
  name?: string;
  last_name?: string;
  role: string;
  status: 'active' | 'pending' | 'deactivated';
  joinedAt?: string;
  invitedAt?: string;
}

export interface WorkspaceProject {
  _id: string;
  name: string;
  workspaceId: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  createdAt: string;
  updatedAt: string;
  responseCount?: number;
  participantCount?: number;
}

export interface WorkspaceFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  sortBy?: 'name' | 'organizationName' | 'createdAt' | 'memberCount' | 'projectCount';
  sortOrder?: 'asc' | 'desc';
}

export interface ProjectFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'draft' | 'active' | 'completed' | 'archived' | 'all';
}

export interface MemberFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'pending' | 'deactivated' | 'all';
}

export interface CreateWorkspacePayload {
  name: string;
  ownerEmail: string;
}

export const workspacesApi = {
  /**
   * Create a new workspace within an organization
   */
  create: async (organizationId: string, payload: CreateWorkspacePayload): Promise<Workspace> => {
    const { data } = await axiosInstance.post<Workspace>(
      `/organizations/${organizationId}/workspaces`,
      payload
    );
    return data;
  },

  /**
   * Get all workspaces with filters and pagination
   */
  getAll: async (params: WorkspaceFilters): Promise<PaginatedResponse<Workspace>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<Workspace>>('/workspaces', {
      params,
    });
    return data;
  },

  /**
   * Get a single workspace by ID
   */
  getById: async (id: string): Promise<Workspace> => {
    const { data } = await axiosInstance.get<Workspace>(`/workspaces/${id}`);
    return data;
  },

  /**
   * Restore a soft-deleted workspace
   */
  restore: async (id: string, organizationId: string): Promise<Workspace> => {
    const { data } = await axiosInstance.patch<Workspace>(
      `/organizations/${organizationId}/workspaces/${id}/restore`
    );
    return data;
  },

  /**
   * Get all members of a workspace
   */
  getMembers: async (
    id: string,
    params?: MemberFilters
  ): Promise<PaginatedResponse<WorkspaceMember>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<WorkspaceMember>>(
      `/workspaces/${id}/members`,
      { params }
    );
    return data;
  },

  /**
   * Get all projects in a workspace
   */
  getProjects: async (
    id: string,
    organizationId: string,
    params?: ProjectFilters
  ): Promise<PaginatedResponse<WorkspaceProject>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<WorkspaceProject>>(
      `/organizations/${organizationId}/workspaces/${id}/projects`,
      { params }
    );
    return data;
  },

  /**
   * Add a member to a workspace
   */
  addMember: async (id: string, userId: string, role: string): Promise<void> => {
    await axiosInstance.post(`/workspaces/${id}/members`, { userId, role });
  },

  /**
   * Remove a member from a workspace
   */
  removeMember: async (id: string, userId: string): Promise<void> => {
    await axiosInstance.delete(`/workspaces/${id}/members/${userId}`);
  },

  /**
   * Update a member's role in a workspace
   */
  updateMemberRole: async (id: string, userId: string, role: string): Promise<void> => {
    await axiosInstance.patch(`/workspaces/${id}/members/${userId}/role`, { role });
  },
};
